import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import {
  NameValuePair,
  SalesOrder,
  SalesOrderShipment,
  ExportableData
} from '@eda/shared/models/json-xlsx.model';

@Injectable({
  providedIn: 'root',
})
export class JsonXlsxService {

  exportToExcel(jsonData: ExportableData | NameValuePair[] | Record<string, any>): ArrayBuffer {
    const normalizedData = this.normalizeJson(jsonData);
    const fileName = this.detectFileName(jsonData);

    const workbook: XLSX.WorkBook = XLSX.utils.book_new();

    if (normalizedData.length > 0) {
      const linesWorkSheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(normalizedData);
      XLSX.utils.book_append_sheet(workbook, linesWorkSheet, 'Order_lines');
    }

    const shipmentData = this.extractShipmentData(jsonData);
    if (shipmentData.length > 0) {
      const shipmentWorkSheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(shipmentData);
      XLSX.utils.book_append_sheet(workbook, shipmentWorkSheet, 'Shipments');
    }

    return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  }

  private extractShipmentData(data: ExportableData | NameValuePair[] | Record<string, any>): Record<string, any>[] {
    if ('salesorder' in data && data.salesorder?.shipments) {
      return this.flattenShipmentDetails(data.salesorder.shipments);
    }

    if ('salesorderShipments' in data && Array.isArray(data.salesorderShipments)) {
      return this.flattenShipmentDetails(data.salesorderShipments as SalesOrderShipment[]);
    }

    return [];
  }

  private detectFileName(jsonData: ExportableData | NameValuePair[] | Record<string, any>): string {
    const isExportableData = 'salesorder' in jsonData || 'salesorderShipment' in jsonData ? jsonData as ExportableData : null;

    if (isExportableData?.salesorder?.lines && Array.isArray(isExportableData.salesorder.lines)) {
      return 'order_details';
    } else if (isExportableData?.salesorderShipments && Array.isArray(isExportableData.salesorderShipments)) {
      return 'order_details';
    }

    return 'product_details';
  }

  private normalizeJson(data: ExportableData | NameValuePair[] | Record<string, any>): Record<string, any>[] {
    const isExportableData = 'salesorder' in data || 'salesorderShipment' in data ? data as ExportableData : null;

    if (Array.isArray(data) && data.length > 0 && data[0] !== null && typeof data[0] === 'object') {
      return data;
    }

    if (Array.isArray(data) && data.length > 0 && data[0]?.name && data[0]?.value) {
      const row: any = {};
      data.forEach((item: any) => {
        row[item.name] = item.value;
      });
      return [row];
    }

    if (isExportableData?.salesorder?.lines && Array.isArray(isExportableData.salesorder.lines)) {
      return this.flattenSalesOrderLines(isExportableData.salesorder);
    }

    if (isExportableData?.salesorderShipments && Array.isArray(isExportableData.salesorderShipments)) {
      return this.flattenShipmentDetails(isExportableData.salesorderShipments);
    }

    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return [data];
    }

    return [];
  }

  private flattenSalesOrderLines(salesorder: SalesOrder): Record<string, any>[] {
    return salesorder.lines.map((line) => ({
      orderNumber: salesorder.orderNumber,
      orderDate: salesorder.orderDate,
      customerPONumber: salesorder.customerPONumber,
      lineNumber: line.lineNumber,
      productCode: line.productCode,
      catalogNumber: line.catalogNumber,
      productDescription: line.productDescription,
      quantityOrdered: line.quantityOrdered,
      quantityShipped: line.quantityShipped,
      quantityOpen: line.quantityOpen,
      universalProductCode: line.universalProductCode,
      shipFormDesc: line.shipFormDesc,
      manufacturingStatus: line.manufacturingStatus,
      lastShipDate: line.lastShipDate
    }));
  }

  private flattenShipmentDetails(shipments: SalesOrderShipment[]): Record<string, any>[] {
    const allDetails: Record<string, any>[] = [];
    shipments.forEach(shipment => {
      if (shipment.shipmentDetails && Array.isArray(shipment.shipmentDetails)) {
        shipment.shipmentDetails.forEach((detail) => {
          allDetails.push({
            shipmentID: shipment.shipmentID,
            shipmentDate: shipment.shipmentDate,
            carrierName: shipment.carrierName,
            orderLineNumber: detail.orderLineNumber,
            catalogNumber: detail.catalogNumber,
            productDescription: detail.productDescription,
            quantityShipped: detail.quantityShipped,
            universalProductCode: detail.universalProductCode,
            trackingNumber: detail.trackingInfo?.[0]?.trackingNumber || '',
            manufacturerPartNumber: detail.manufacturerPartNumber
          });
        });
      }
    });

    return allDetails;
  }
}
